import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Dialog from "./dialog/Dialog";
import {
  acceptSupplierPlatformTerms,
  getSupplierPlatformTerms,
} from "../services/supplierTermsService";
import {
  hasAcceptedPlatformTerms,
  syncSupplierUser,
} from "../utils/supplierServices";

function updateStoredTermsAcceptance(termsAcceptedAt, userPayload = null) {
  if (userPayload) {
    syncSupplierUser(userPayload);
    return;
  }

  try {
    const userStr = localStorage.getItem("user");
    if (!userStr) return;

    const user = JSON.parse(userStr);
    const detailsKey = user.supplier_details ? "supplier_details" : "supplierDetails";
    const details = user[detailsKey] || {};
    details.terms_accepted_at = termsAcceptedAt || new Date().toISOString();
    user[detailsKey] = details;
    syncSupplierUser(user);
  } catch {
    // Ignore localStorage parse errors; server state is already saved.
  }
}

function SupplierTermsGate({ children }) {
  const [agreed, setAgreed] = useState(false);
  const [accepted, setAccepted] = useState(() => {
    try {
      const userStr = localStorage.getItem("user");
      if (!userStr) return true;
      return hasAcceptedPlatformTerms(JSON.parse(userStr));
    } catch {
      return true;
    }
  });

  const shouldCheckTerms = useMemo(() => !accepted, [accepted]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["supplierPlatformTerms"],
    queryFn: getSupplierPlatformTerms,
    enabled: shouldCheckTerms,
    retry: 1,
  });

  const termsPayload = data?.data ?? data ?? {};
  const requiresAcceptance =
    shouldCheckTerms &&
    !isError &&
    (termsPayload.requires_acceptance === true ||
      termsPayload.terms_accepted_at == null);

  useEffect(() => {
    if (!shouldCheckTerms || !termsPayload.terms_accepted_at) {
      return;
    }

    updateStoredTermsAcceptance(termsPayload.terms_accepted_at);
    setAccepted(true);
  }, [shouldCheckTerms, termsPayload.terms_accepted_at]);

  const acceptMutation = useMutation({
    mutationFn: acceptSupplierPlatformTerms,
    onSuccess: (response) => {
      const payload = response?.data ?? response ?? {};
      updateStoredTermsAcceptance(payload.terms_accepted_at, payload.user || null);
      setAccepted(true);
      setAgreed(false);
    },
  });

  if (accepted) {
    return children;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          <p className="text-sm text-gray-600">Loading supplier portal...</p>
        </div>
      </div>
    );
  }

  if (!requiresAcceptance) {
    return children;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Dialog
        open
        onClose={() => {}}
        title="Terms & Conditions"
        maxWidth="2xl"
        width="w-full max-w-3xl"
        maxHeight="55vh"
        showCloseButton={false}
        preventOutsideClick
        primaryButtonText={
          acceptMutation.isPending ? "Saving..." : "I Agree & Continue"
        }
        primaryButtonAction={() => acceptMutation.mutate()}
        isLoading={acceptMutation.isPending}
        isPrimaryDisabled={!agreed || acceptMutation.isPending}
      >
        <div className="space-y-4 px-2">
          {isError ? (
            <div className="rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700">
              Unable to load terms and conditions. Please refresh the page or
              contact support.
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                Please review and accept the platform terms before using the
                supplier portal for the first time.
              </p>
              <div
                className="prose prose-sm max-w-none text-gray-700"
                dangerouslySetInnerHTML={{
                  __html: termsPayload.content || "",
                }}
              />
              <label className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(event) => setAgreed(event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  I have read and agree to the Supplier Terms &amp; Conditions.
                </span>
              </label>
              {acceptMutation.isError && (
                <p className="text-sm text-red-600">
                  {acceptMutation.error?.message ||
                    "Failed to save your acceptance. Please try again."}
                </p>
              )}
            </>
          )}
        </div>
      </Dialog>
    </div>
  );
}

export default SupplierTermsGate;
